export type YesNo = 'yes' | 'no';

export type DependentRelationship = 'child' | 'father' | 'mother' | 'other';

export type DisabilityCategory = 'none' | 'general' | 'special';

/** 'none' = non-resident with no qualifying support condition. */
export type DependentNonResidentReason =
  | 'study_abroad'
  | 'disability'
  | 'remittance_380k'
  | 'none';

export type DependentIneligibilityReason =
  | 'no_shared_livelihood'
  | 'income_over_limit'
  | 'non_resident_no_support'
  | '';

export const DEPENDENT_CHANGE_REASON_CODES = [
  'birth',
  'death',
  'became_dependent',
  'no_longer_dependent',
  'moved_overseas',
  'returned_japan',
  'other',
] as const;

export type DependentChangeReasonCode = (typeof DEPENDENT_CHANGE_REASON_CODES)[number];

export function isDependentChangeReasonCode(value: string): value is DependentChangeReasonCode {
  return (DEPENDENT_CHANGE_REASON_CODES as readonly string[]).includes(value);
}

/**
 * Age-derived dependent classification stored in tax_dependent_category.
 * under16 = 16歳未満の扶養親族,
 * specific = 特定扶養親族 (19–22, income ≤ 58万),
 * specific_relative = 特定親族 (19–22, income 58万超–100万以下),
 * elderly = 老人扶養親族 (70+),
 * cohabiting_elderly_parents = 同居老親等,
 * general = 上記以外の源泉控除対象親族.
 */
export type DependentTaxCategory =
  | 'under16'
  | 'specific'
  | 'specific_relative'
  | 'elderly'
  | 'cohabiting_elderly_parents'
  | 'general'
  | 'non_residents';

export type DependentsStep =
  | 'hasDependents'
  | 'addName'
  | 'addKana'
  | 'addRelationship'
  | 'addRelationshipOther'
  | 'addDob'
  | 'confirmFullName'
  | 'confirmFullNameKana'
  | 'confirmRelationship'
  | 'myNumber'
  | 'sameHousehold'
  | 'addressIfNot'
  | 'sharedLivelihood'
  | 'estimatedIncome'
  | 'ineligibleNotice'
  | 'ageCategoryNotice'
  | 'nonResident'
  | 'nonResidentReason'
  | 'remittanceAmount'
  | 'disabilityStatus'
  | 'changeDuringYear'
  | 'changeDate'
  | 'changeReason'
  | 'changeReasonOther'
  | 'addAnother'
  | 'finalConfirm';

/** Per-dependent answers; identity fields prefilled from Family Info when available. */
export type DependentDraft = {
  empFamilyId: number | null;
  fullName: string;
  fullNameKana: string;
  /** Raw relationship string from emp_family (may be outside the picker options). */
  relationship: string;
  relationshipOther: string;
  dateOfBirth: string;
  myNumber: string;
  livingInSameHousehold: YesNo | '';
  ifNotAddress: string;
  sharedLivelihood: YesNo | '';
  estimatedIncome: string;
  nonResident: YesNo | '';
  nonResidentReason: DependentNonResidentReason | '';
  remittanceAmount: string;
  disabilityCategory: DisabilityCategory | '';
  changeDuringYear: YesNo | '';
  changeDate: string;
  /** Preset code or free text when Other. */
  changeReason: string;
  eligible: boolean;
  ineligibilityReason: DependentIneligibilityReason;
  /** True when this person was added inside Section 3 (not from Family Info). */
  addedInSection: boolean;
};

export function emptyDependentDraft(): DependentDraft {
  return {
    empFamilyId: null,
    fullName: '',
    fullNameKana: '',
    relationship: '',
    relationshipOther: '',
    dateOfBirth: '',
    myNumber: '',
    livingInSameHousehold: '',
    ifNotAddress: '',
    sharedLivelihood: '',
    estimatedIncome: '',
    nonResident: '',
    nonResidentReason: '',
    remittanceAmount: '',
    disabilityCategory: '',
    changeDuringYear: '',
    changeDate: '',
    changeReason: '',
    eligible: true,
    ineligibilityReason: '',
    addedInSection: false,
  };
}

/** Collapsed dependent summary + snapshot so Edit can reopen the Q&A. */
export type CompletedDependentEntry = {
  text: string;
  snapshot: DependentDraft;
};
